import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import api from '../api';
import { FileText, Bell, ChevronRight, CheckCircle, Percent, Trash2 } from 'lucide-react';

const _API_URL = process.env.REACT_APP_BACKEND_URL || '';
const fmtCurrency = (v) => `$${Number(v || 0).toFixed(2)}`;
const fmtDate = (d) => d ? new Date(d).toLocaleDateString() : '—';

const AccountStatements = () => {
  const navigate = useNavigate();
  const [customers, setCustomers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [serviceRate, setServiceRate] = useState(1.5);
  const [sendingReminders, setSendingReminders] = useState(false);
  const [reminderMsg, setReminderMsg] = useState('');
  const [daysOverdue, setDaysOverdue] = useState(0);
  const [excludePwo, setExcludePwo] = useState(false);

  useEffect(() => {
    loadCustomers();
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [daysOverdue, excludePwo]);

  const buildParams = () => {
    const params = new URLSearchParams();
    params.append('days_overdue', daysOverdue);
    if (excludePwo) params.append('exclude_pwo', 'true');
    return params.toString();
  };

  const loadCustomers = async () => {
    setLoading(true);
    try {
      const data = await api.request(`/api/statements/customers?${buildParams()}`);
      setCustomers(Array.isArray(data) ? data : []);
    } catch (err) {
      console.error(err);
    } finally {
      setLoading(false);
    }
  };

  const sendReminders = async () => {
    setSendingReminders(true);
    setReminderMsg('');
    try {
      const res = await api.request(`/api/statements/send-reminders?days_overdue=${daysOverdue}${excludePwo ? '&exclude_pwo=true' : ''}`, { method: 'POST' });
      setReminderMsg(`${res.reminders_created} reminder(s) created`);
      setTimeout(() => setReminderMsg(''), 5000);
    } catch (err) {
      setReminderMsg('Failed: ' + err.message);
    } finally {
      setSendingReminders(false);
    }
  };

  const deleteStatement = async (customerName, e) => {
    e.stopPropagation();
    if (!window.confirm(`Delete account statement for ${customerName}? This will mark all their pending invoices as paid/cleared.`)) return;
    try {
      await api.request(`/api/statements/mark-all-paid/${encodeURIComponent(customerName)}`, { method: 'PUT' });
      loadCustomers();
    } catch (err) {
      alert('Failed: ' + err.message);
    }
  };

  const totalOwed = customers.reduce((sum, c) => sum + c.total_owed, 0);

  return (
    <div className="space-y-6" data-testid="account-statements-page">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold flex items-center gap-2">
            <FileText className="h-6 w-6" /> Account Statements
          </h1>
          <p className="text-sm text-muted-foreground mt-1">Customers with unpaid invoices</p>
        </div>
        <div className="flex items-center gap-3">
          <div className="flex items-center gap-2 bg-card border rounded-lg px-3 py-2">
            <Percent className="h-4 w-4 text-muted-foreground" />
            <label className="text-sm text-muted-foreground">Service Charge:</label>
            <input
              type="number" step="0.1" min="0" max="10"
              value={serviceRate}
              onChange={e => setServiceRate(parseFloat(e.target.value) || 0)}
              className="w-16 px-2 py-1 border border-input rounded text-sm text-center"
              data-testid="service-rate-input"
            />
            <span className="text-sm text-muted-foreground">% / month</span>
          </div>
          <button
            onClick={sendReminders}
            disabled={sendingReminders}
            className="flex items-center gap-2 px-4 py-2 bg-amber-600 text-white rounded-lg hover:bg-amber-700 disabled:opacity-50 text-sm font-medium"
            data-testid="send-reminders-btn"
          >
            <Bell className="h-4 w-4" />
            {sendingReminders ? 'Sending...' : 'Send Reminders'}
          </button>
        </div>
      </div>

      {/* Filters */}
      <div className="flex items-center gap-4 bg-card border rounded-lg px-4 py-3" data-testid="statement-filters">
        <div className="flex items-center gap-2">
          <label className="text-sm font-medium text-muted-foreground">Min Days Overdue:</label>
          <select
            value={daysOverdue}
            onChange={e => setDaysOverdue(parseInt(e.target.value))}
            className="px-3 py-1.5 border border-input rounded text-sm bg-background"
            data-testid="days-overdue-filter"
          >
            <option value={0}>All Unpaid</option>
            <option value={15}>15+ Days</option>
            <option value={30}>30+ Days</option>
            <option value={60}>60+ Days</option>
            <option value={90}>90+ Days</option>
          </select>
        </div>
        <div className="flex items-center gap-2">
          <input
            type="checkbox"
            id="exclude-pwo"
            checked={excludePwo}
            onChange={e => setExcludePwo(e.target.checked)}
            className="rounded border-input"
            data-testid="exclude-pwo-filter"
          />
          <label htmlFor="exclude-pwo" className="text-sm text-muted-foreground">Exclude PWO Price/Cost</label>
        </div>
      </div>

      {reminderMsg && (
        <div className="bg-green-50 border border-green-200 text-green-700 px-4 py-2 rounded-lg text-sm" data-testid="reminder-msg">
          {reminderMsg}
        </div>
      )}

      {/* Summary Cards */}
      <div className="grid grid-cols-3 gap-4">
        <div className="bg-card border rounded-lg p-4">
          <div className="text-xs text-muted-foreground uppercase">Customers with Unpaid</div>
          <div className="text-2xl font-bold mt-1" data-testid="overdue-customer-count">{customers.length}</div>
        </div>
        <div className="bg-card border rounded-lg p-4">
          <div className="text-xs text-muted-foreground uppercase">Total Overdue Invoices</div>
          <div className="text-2xl font-bold mt-1">{customers.reduce((s, c) => s + c.invoice_count, 0)}</div>
        </div>
        <div className="bg-card border rounded-lg p-4">
          <div className="text-xs text-muted-foreground uppercase">Total Amount Owed</div>
          <div className="text-2xl font-bold text-red-600 mt-1" data-testid="total-owed">{fmtCurrency(totalOwed)}</div>
        </div>
      </div>

      {/* Customer List */}
      <div className="bg-card border rounded-lg overflow-hidden">
        <table className="w-full" data-testid="customers-table">
          <thead>
            <tr className="border-b bg-muted/30">
              <th className="w-8 px-2"></th>
              <th className="text-left px-4 py-3 text-xs font-medium text-muted-foreground uppercase">Customer</th>
              <th className="text-left px-4 py-3 text-xs font-medium text-muted-foreground uppercase">Pricing</th>
              <th className="text-right px-4 py-3 text-xs font-medium text-muted-foreground uppercase">Pending</th>
              <th className="text-right px-4 py-3 text-xs font-medium text-muted-foreground uppercase">Paid</th>
              <th className="text-right px-4 py-3 text-xs font-medium text-muted-foreground uppercase">Total Owed</th>
              <th className="text-left px-4 py-3 text-xs font-medium text-muted-foreground uppercase">Oldest Invoice</th>
              <th className="text-center px-4 py-3 text-xs font-medium text-muted-foreground uppercase w-16">Delete</th>
            </tr>
          </thead>
          <tbody className="divide-y divide-border">
            {loading ? (
              <tr><td colSpan="8" className="px-4 py-8 text-center text-muted-foreground">Loading...</td></tr>
            ) : customers.length === 0 ? (
              <tr><td colSpan="8" className="px-4 py-8 text-center text-muted-foreground">
                <CheckCircle className="h-8 w-8 mx-auto mb-2 text-green-500" />
                No unpaid invoices found
              </td></tr>
            ) : customers.map(c => (
              <tr
                key={c.customer_name}
                className="hover:bg-muted/20 cursor-pointer"
                onClick={() => navigate(`/account-statements/${encodeURIComponent(c.customer_name)}`)}
                data-testid={`customer-row-${c.customer_name}`}
              >
                <td className="px-2 py-3 text-center">
                  <ChevronRight className="h-4 w-4 inline text-muted-foreground" />
                </td>
                <td className="px-4 py-3 font-medium text-primary">{c.customer_name}</td>
                <td className="px-4 py-3">
                  <span className={`text-xs px-1.5 py-0.5 rounded ${c.pricing_scheme === 'PWO Price/Cost' ? 'bg-amber-100 text-amber-700' : 'bg-blue-100 text-blue-700'}`}>
                    {c.pricing_scheme || 'Regular'}
                  </span>
                </td>
                <td className="px-4 py-3 text-right tabular-nums">{c.pending_count || 0}</td>
                <td className="px-4 py-3 text-right tabular-nums text-green-600">{c.paid_count || 0}</td>
                <td className="px-4 py-3 text-right tabular-nums font-semibold text-red-600">
                  {fmtCurrency(c.total_owed)}
                  {c.return_credits > 0 && (
                    <div className="text-xs font-normal text-red-500">({fmtCurrency(c.return_credits)} credits)</div>
                  )}
                </td>
                <td className="px-4 py-3 text-sm text-muted-foreground">{fmtDate(c.oldest_invoice_date)}</td>
                <td className="px-4 py-3 text-center">
                  <button
                    onClick={(e) => deleteStatement(c.customer_name, e)}
                    className="p-1.5 text-red-500 hover:bg-red-50 rounded-md"
                    title="Delete statement"
                    data-testid={`delete-statement-${c.customer_name}`}
                  >
                    <Trash2 className="h-4 w-4" />
                  </button>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
};

export default AccountStatements;
